import { HTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  lines?: number;
}

const variantStyles: Record<SkeletonVariant, string> = {
  text: 'rounded',
  circular: 'rounded-full',
  rectangular: '',
  rounded: 'rounded-lg',
};

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = 'rectangular',
      width,
      height,
      lines = 3,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const inlineStyle: React.CSSProperties = { ...style };
    let widthClass = '';
    let heightClass = '';

    if (typeof width === 'number') {
      inlineStyle.width = `${width}px`;
    } else if (typeof width === 'string') {
      widthClass = width;
    }

    if (typeof height === 'number') {
      inlineStyle.height = `${height}px`;
    } else if (typeof height === 'string') {
      heightClass = height;
    }

    if (variant === 'text') {
      return (
        <div
          ref={ref}
          className={clsx('animate-pulse space-y-2', className)}
          {...props}
        >
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className={clsx(
                'h-4 rounded skeleton-shimmer',
                index === lines - 1 ? 'w-3/4' : 'w-full'
              )}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={clsx(
          'animate-pulse skeleton-shimmer',
          variantStyles[variant],
          widthClass,
          heightClass,
          className
        )}
        style={inlineStyle}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';
